import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export interface ConfirmActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  actionLabel: string;
  destructive?: boolean;
  /** When set, the action stays disabled until the user types this exact value. */
  confirmName?: string;
  pending?: boolean;
  onConfirm: () => void | Promise<void>;
}

// Shared confirm for all destructive platform actions. Uses a plain Button
// instead of AlertDialogAction because Radix auto-closes on Action click —
// async actions need the dialog to stay open until the caller resolves.
export function ConfirmActionDialog({
  open,
  onOpenChange,
  title,
  description,
  actionLabel,
  destructive,
  confirmName,
  pending,
  onConfirm,
}: ConfirmActionDialogProps) {
  const [typed, setTyped] = useState("");

  // Stale input from a previous open must not pre-arm the action button.
  useEffect(() => {
    if (!open) setTyped("");
  }, [open]);

  const nameMismatch = confirmName !== undefined && typed !== confirmName;

  return (
    <AlertDialog open={open} onOpenChange={(next) => { if (!pending) onOpenChange(next); }}>
      <AlertDialogContent className="glass glass-border max-w-[calc(100vw-2rem)] rounded-xl sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {confirmName !== undefined && (
          <div className="space-y-2">
            <Label htmlFor="confirm-name" className="text-xs text-muted-foreground">
              Type <span className="font-mono text-foreground">{confirmName}</span> to confirm
            </Label>
            <Input
              id="confirm-name"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={confirmName}
              autoComplete="off"
              spellCheck={false}
              className="font-mono"
              disabled={pending}
            />
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
          <Button
            variant={destructive ? "destructive" : "default"}
            disabled={pending || nameMismatch}
            onClick={() => void onConfirm()}
          >
            {pending && <Loader2 className="h-4 w-4 animate-spin" />}
            {actionLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
